// Currency conversion utilities for VIP users

// Dynamic exchange rate - will be fetched from API
let USD_TO_TRY_RATE = 42.50; // Default fallback

/**
 * Fetch current USD/TRY rate from backend
 */
export const fetchExchangeRate = async () => {
  try {
    const API = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
      ? 'http://localhost:8001/api' 
      : '/api';
    
    const response = await fetch(`${API}/exchange-rate`);
    if (response.ok) {
      const data = await response.json();
      USD_TO_TRY_RATE = data.usd_try_rate || 42.50;
      console.log('✅ Exchange rate updated:', USD_TO_TRY_RATE);
    }
  } catch (error) {
    console.warn('⚠️ Could not fetch exchange rate, using fallback:', USD_TO_TRY_RATE);
  }
  return USD_TO_TRY_RATE;
};

/**
 * Get current exchange rate
 */
export const getExchangeRate = () => USD_TO_TRY_RATE;

/**
 * Set exchange rate (for manual updates)
 */
export const setExchangeRate = (rate) => {
  USD_TO_TRY_RATE = rate;
};

/**
 * Convert TRY to USD
 */
export const tryToUsd = (amountInTry) => {
  return amountInTry / USD_TO_TRY_RATE;
};

/**
 * Convert USD to TRY
 */
export const usdToTry = (amountInUsd) => {
  return amountInUsd * USD_TO_TRY_RATE;
};

/**
 * Format currency based on asset currency or user account type
 * @param {number} amount - Amount 
 * @param {object} user - User object with account_type
 * @param {boolean} showSymbol - Whether to show currency symbol
 * @param {string} currency - Currency code (USD or TRY)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, user, showSymbol = true, currency = null) => {
  // Handle null/undefined amounts
  let safeAmount = amount ?? 0;
  
  // Scientific notation'ı düzgün sayıya çevir
  if (typeof safeAmount === 'string' || (typeof safeAmount === 'number' && safeAmount.toString().includes('e'))) {
    safeAmount = Number(safeAmount);
  }
  
  // Küçük fiyatlar için daha fazla ondalık basamak
  const getDecimalPlaces = (value) => {
    const absValue = Math.abs(value);
    if (absValue === 0) return 2;
    if (absValue < 0.00001) return 10;
    if (absValue < 0.0001) return 8;
    if (absValue < 0.001) return 6;
    if (absValue < 0.01) return 5;
    if (absValue < 0.1) return 4;
    if (absValue < 1) return 3;
    return 2; // 1 ve üzeri için 2 ondalık basamak
  };
  
  const decimals = getDecimalPlaces(safeAmount);
  
  // Küçük sayıları manuel formatlama ile göster (toLocaleString bazen bilimsel gösterim verir)
  const formatSmallNumber = (num, decPlaces, locale) => {
    if (num === 0) return locale === 'tr-TR' ? '0,00' : '0.00';
    
    // Sayıyı string'e çevir ve fixed kullan
    const fixed = num.toFixed(decPlaces);
    
    // Locale'e göre formatla
    if (locale === 'tr-TR') {
      // TR format: 1.234,56
      const [intPart, decPart] = fixed.split('.');
      const formattedInt = parseInt(intPart).toLocaleString('tr-TR');
      return decPart ? `${formattedInt},${decPart}` : formattedInt;
    } else {
      // US format: 1,234.56
      const [intPart, decPart] = fixed.split('.');
      const formattedInt = parseInt(intPart).toLocaleString('en-US');
      return decPart ? `${formattedInt}.${decPart}` : formattedInt;
    }
  };
  
  // If currency is explicitly USD (from asset), show as USD
  if (currency === 'USD') {
    const formatted = formatSmallNumber(safeAmount, decimals, 'en-US');
    return showSymbol ? `$${formatted}` : formatted;
  }
  
  // VIP users see USD for their balance/portfolio
  const isVip = user?.account_type === 'VIP';
  if (isVip && !currency) {
    const amountInUsd = tryToUsd(safeAmount);
    const usdDecimals = getDecimalPlaces(amountInUsd);
    const formatted = formatSmallNumber(amountInUsd, usdDecimals, 'en-US');
    return showSymbol ? `$${formatted}` : formatted;
  }
  
  // Default: TRY (₺)
  const formatted = formatSmallNumber(safeAmount, decimals, 'tr-TR');
  return showSymbol ? `₺${formatted}` : formatted;
};

/**
 * Get currency symbol based on user type
 */
export const getCurrencySymbol = (user) => {
  return user?.account_type === 'VIP' ? '$' : '₺';
};

/**
 * Get currency code based on user type
 */
export const getCurrencyCode = (user) => {
  return user?.account_type === 'VIP' ? 'USD' : 'TRY';
};

/**
 * Get actual amount in user's preferred currency
 */
export const getDisplayAmount = (amountInTry, user) => {
  const isVip = user?.account_type === 'VIP';
  return isVip ? tryToUsd(amountInTry) : amountInTry;
};
